import React from "react";
import {Link} from "react-router-dom";
import {Button, Tooltip} from "antd";
import {ColumnsType} from "antd/lib/table";
import {TFunction} from "i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAngleDown, faCartPlus, faCheckCircle, faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import {CartText} from "../../cart.messages";
import {Warehouse} from "../../../../models/warehoues/warehouse";
import {Price} from "../../../core/commons/price/price";
import {CartHintRow, formatHintQty} from "./cart-create-hints.helpers";

const typeKeys = {
    pcs: ["PCS", "PCS_HELP"],
    kit: ["KIT", "KIT_HELP"],
    replacement: ["REPLACEMENT", "REPLACEMENT_HELP"],
} as const;

export const hintExplanation = ({item, coverage}: CartHintRow, text: CartText): string =>
    !coverage.hasStock ? text("NO_STOCK_DATA") : text(coverage.isFullyReplaceable ? "FULL_EXPLANATION" : "PARTIAL_EXPLANATION", {
        requested: formatHintQty(item.requestedQty),
        original: item.requestedNo,
        required: formatHintQty(coverage.requiredQty),
        alternative: item.article.name,
        available: formatHintQty(coverage.availableQty),
    });

export const HintDetails: React.FC<{row: CartHintRow; warehouses: Warehouse[]; text: CartText}> = ({row, warehouses, text}) => (
    <div className="hint-details" id={`hint-details-${encodeURIComponent(row.key)}`}>
        <div className="hint-details-copy">
            <strong>{row.item.article.name}</strong>
            <p>{row.item.article.description}</p>
            <p className={row.coverage.isFullyReplaceable ? "is-full" : "is-partial"}>{hintExplanation(row, text)}</p>
            {row.coverage.replacementQtyFactor ? <p>{text("RATIO", {
                alternative: row.item.article.name,
                factor: row.coverage.replacementQtyFactor,
                original: row.item.requestedNo,
            })}</p> : null}
        </div>
        {row.stocks.length > 0 && (
            <div className="hint-details-stocks">
                <div className="hint-details-stock hint-details-stock-labels">
                    <span>{text("WAREHOUSE")}</span><span>{text("AVAILABLE")} / {text("REQUIRED")}</span>
                </div>
                {row.stocks.map(stock => (
                    <div className="hint-details-stock" key={stock.warehouse}>
                        <span>{warehouses.find(warehouse => warehouse.id === stock.warehouse)?.name || stock.warehouse.toUpperCase()}</span>
                        <strong className={stock.fullyQtyReplaceable ? "is-full" : "is-partial"}>
                            {formatHintQty(stock.availableQty)} / {formatHintQty(stock.requiredQty)}
                        </strong>
                    </div>
                ))}
            </div>
        )}
    </div>
);

interface ColumnsOptions {
    t: TFunction;
    text: CartText;
    addToCart: (row: CartHintRow) => void;
    toggleDetails: (row: CartHintRow) => void;
    expandedKey: string | null;
    loadingKey: string | null;
    disabled: boolean;
}

export const columnsFactory = ({t, text, addToCart, toggleDetails, expandedKey, loadingKey, disabled}: ColumnsOptions): ColumnsType<CartHintRow> => [
    {
        title: text("REQUESTED"),
        key: "requested",
        width: "22%",
        className: "hint-request-column",
        render: (_, {item}) => (
            <div className="hint-inline-product" title={item.requestedNo}>
                <strong className="hint-sku">{item.requestedNo}</strong>
                <span className="hint-request-qty">× {formatHintQty(item.requestedQty)}</span>
            </div>
        ),
    },
    {
        title: text("ALTERNATIVE"),
        key: "alternative",
        className: "hint-alternative-column",
        render: (_, {item, coverage}) => {
            const type = coverage.type && typeKeys[coverage.type];
            return (
                <div className="hint-inline-product">
                    <Link className="link hint-sku" to={`/catalog/article/${item.article.name}`} title={item.article.description}>
                        {item.article.name}
                    </Link>
                    {type && <Tooltip title={text(type[1])}>
                        <span className={`hint-type hint-type-${coverage.type}`} tabIndex={0}>{text(type[0])}</span>
                    </Tooltip>}
                </div>
            );
        },
    },
    {
        title: String(t("PRICE")),
        key: "price",
        width: "11%",
        align: "right",
        className: "hint-price-column",
        render: (_, {item}) => item.calculatedPrice ? <Price value={item.calculatedPrice.amount} /> : "—",
    },
    {
        title: text("REQUIRED"),
        key: "required",
        width: "13%",
        align: "right",
        className: "hint-required-column",
        render: (_, row) => (
            <Tooltip title={hintExplanation(row, text)}>
                <span className="hint-required" tabIndex={0}>
                    {row.coverage.hasStock ? formatHintQty(row.coverage.requiredQty) : "—"}
                    {row.coverage.replacementQtyFactor && row.coverage.replacementQtyFactor !== 1
                        ? <small> ÷{row.coverage.replacementQtyFactor}</small> : null}
                </span>
            </Tooltip>
        ),
    },
    {
        title: text("AVAILABLE"),
        key: "available",
        width: "14%",
        align: "right",
        className: "hint-stock-column",
        render: (_, row) => (
            <Tooltip title={hintExplanation(row, text)}>
                <span className={`hint-stock ${row.coverage.isFullyReplaceable ? "is-full" : "is-partial"}`} tabIndex={0}>
                    <FontAwesomeIcon icon={row.coverage.isFullyReplaceable ? faCheckCircle : faExclamationTriangle} />
                    {row.coverage.hasStock ? formatHintQty(row.coverage.availableQty) : "—"}
                </span>
            </Tooltip>
        ),
    },
    {
        title: <span className="cart-hint-sr-only">{String(t("ACTION"))}</span>,
        key: "action",
        width: 82,
        className: "hint-actions-column",
        render: (_, row) => {
            const addLabel = row.coverage.qtyToAdd > 0
                ? text("ADD", {qty: row.coverage.qtyToAdd, article: row.item.article.name})
                : text(row.coverage.hasStock ? "OUT_OF_STOCK" : "NO_STOCK_DATA");
            return (
                <div className="hint-row-actions">
                    <Button
                        type="text"
                        size="small"
                        aria-label={text("DETAILS", {article: row.item.article.name})}
                        aria-expanded={expandedKey === row.key}
                        aria-controls={expandedKey === row.key ? `hint-details-${encodeURIComponent(row.key)}` : undefined}
                        icon={<FontAwesomeIcon icon={faAngleDown} rotation={expandedKey === row.key ? 180 : undefined} />}
                        onClick={() => toggleDetails(row)}
                    />
                    <Tooltip title={addLabel}>
                        <Button
                            type={row.coverage.isFullyReplaceable ? "primary" : "default"}
                            size="small"
                            aria-label={addLabel}
                            className={`hint-add-button ${row.coverage.isFullyReplaceable ? "is-full" : "is-partial"}`}
                            disabled={disabled || row.coverage.qtyToAdd <= 0}
                            loading={loadingKey === row.key}
                            icon={<FontAwesomeIcon icon={faCartPlus} />}
                            onClick={() => addToCart(row)}
                        />
                    </Tooltip>
                </div>
            );
        },
    },
];
